/* Discount codes in the cart drawer.
   POST /cart/update.js takes a comma-separated `discount` string, so adding one code means
   resending every code, and removing one means resending the rest. An unknown code still
   returns 200 with the cart unchanged, so success is decided by inspecting the response. */
(() => {
  const SECTIONS = 'cart-drawer,cart-bubble,main-cart';
  const TARGETS = [
    ['Cart-Drawer', 'cart-drawer', '.cart-drawer'],
    ['cart-drawer-toggle', 'cart-bubble', '.thb-item-count'],
    ['Cart', 'main-cart', '.thb-cart-form'],
  ];
  const LINGER_MS = 4000;
  const FADE_MS = 300;

  let hideTimer;

  const bar = () => document.querySelector('[data-discount-bar]');

  /* The AJAX cart nests type/title under `discount_application`, unlike the flat Liquid object.
     `discount_codes` carries {code, applicable} and is the direct answer, so prefer it. */
  const codesIn = (cart) => {
    if (Array.isArray(cart.discount_codes)) {
      return cart.discount_codes.filter((d) => d.applicable).map((d) => d.code);
    }
    return (cart.cart_level_discount_applications || [])
      .map((entry) => entry.discount_application || entry)
      .filter((d) => d.type === 'discount_code')
      .map((d) => d.title);
  };

  /* timed rather than animationend, so it still hides under prefers-reduced-motion
     where the fade-out animation never runs */
  const dismiss = (el) => {
    if (!el || el.hidden || 'leaving' in el.dataset) return;
    el.dataset.leaving = '';
    setTimeout(() => {
      el.hidden = true;
      delete el.dataset.leaving;
    }, FADE_MS);
  };

  // the bar is absolutely positioned, so showing it never changes the header's height
  const flash = (text, state, autoHide) => {
    const el = bar();
    if (!el) return;
    clearTimeout(hideTimer);
    delete el.dataset.leaving;
    el.textContent = text;
    el.dataset.state = state;
    el.hidden = false;
    if (autoHide) hideTimer = setTimeout(() => dismiss(bar()), LINGER_MS);
  };

  const busy = (state) => {
    const button = document.querySelector('[data-discount-apply]');
    if (!button) return;
    button.disabled = state;
    button.setAttribute('aria-busy', String(state));
  };

  // matches the theme's own section swap in app.js so the drawer rebuilds the same way
  const render = (state) => {
    TARGETS.forEach(([id, section, selector]) => {
      const host = document.getElementById(id);
      if (!host || !state.sections || !state.sections[section]) return;
      const fresh = new DOMParser()
        .parseFromString(state.sections[section], 'text/html')
        .querySelector(selector);
      if (!fresh) return;
      (host.querySelector(selector) || host).innerHTML = fresh.innerHTML;
    });

    const drawer = document.getElementById('Cart-Drawer');
    drawer?.notesToggle?.();
    drawer?.removeProductEvent?.();
  };

  const send = (codes) =>
    fetch(`${window.theme?.routes?.cart_update_url || '/cart/update'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(','), sections: SECTIONS }),
    }).then((response) => response.json());

  const currentCodes = (form) => (form.dataset.codes || '').split(',').filter(Boolean);

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-discount-form]');
    if (!form) return;
    event.preventDefault();

    const input = form.querySelector('[data-discount-input]');
    const code = input.value.trim();
    const applied = form.dataset.state === 'applied';

    if (!applied && !code) return;

    busy(true);

    const next = applied
      ? currentCodes(form).filter((c) => c.toLowerCase() !== code.toLowerCase())
      : [...currentCodes(form), code];

    send(next)
      .then((cart) => {
        const live = codesIn(cart).some((c) => c.toLowerCase() === code.toLowerCase());
        render(cart);

        if (applied) {
          flash('Borttagen', 'warn', true);
          return;
        }
        if (live) {
          flash('Giltig', 'ok', true);
          return;
        }

        // Shopify keeps a rejected code in discount_codes with applicable:false, so a known
        // code that simply cannot apply yet is distinguishable from one that does not exist
        const known = (cart.discount_codes || []).some(
          (d) => d.code.toLowerCase() === code.toLowerCase()
        );

        /* On an empty cart Shopify still stores the code and applies it by itself once
           something is added, so this is accepted, not rejected. Liquid cannot see a stored
           code, so the field is refilled here rather than by the re-render. */
        if (known && cart.item_count === 0) {
          flash('Sparad – gäller vid köp', 'warn', true);
          const saved = document.querySelector('[data-discount-input]');
          if (saved) saved.value = code;
          return;
        }

        flash(known ? 'Villkoren uppfylls inte' : 'Ogiltig kod', 'error', true);
        const retry = document.querySelector('[data-discount-input]');
        if (retry) {
          retry.value = code;
          retry.focus();
        }
      })
      .catch(() => flash('Något gick fel', 'error', true))
      .finally(() => busy(false));
  });

  // typing again clears a stale verdict
  document.addEventListener('input', (event) => {
    if (!event.target.closest('[data-discount-input]')) return;
    const el = bar();
    if (el && el.dataset.state === 'error') {
      clearTimeout(hideTimer);
      dismiss(el);
    }
  });
})();
